/**
 * @file FloatButton 悬浮按钮
 * @description 固定在视口右下角的圆形操作入口，支持按钮或路由链接。
 *
 * @whenToUse 全局快捷入口（如开发态进预览、回到顶部、新建）。
 * @whenNotToUse
 * 1. 工具栏 / 表单内常规操作（用 Button）。
 * 2. 需要大段文案的主 CTA（用实心 Button 放在内容流中）。
 */

import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { Link } from 'react-router-dom'

type FloatButtonShared = {
  /** 按钮内图标（lucide-react） */
  icon: ReactNode
  /** 悬停时在左侧显示的短文案 */
  label?: string
  /** 追加 class，可用于预览态覆盖定位（如 `static!`） */
  className?: string
  /** 无障碍名称（必填） */
  'aria-label': string
}

type FloatButtonAsButton = FloatButtonShared &
  Omit<
    ButtonHTMLAttributes<HTMLButtonElement>,
    'children' | 'className' | 'aria-label'
  > & {
    to?: undefined
  }

type FloatButtonAsLink = FloatButtonShared & {
  /** 作为路由链接时的目标路径 */
  to: string
}

export type FloatButtonProps = FloatButtonAsButton | FloatButtonAsLink

const floatClassName =
  'group fixed right-6 bottom-6 z-50 inline-flex size-12 items-center justify-center rounded-full border-2 border-primary bg-primary text-primary-foreground transition-colors duration-200 hover:opacity-90 focus-visible:opacity-90'

export function FloatButton(props: FloatButtonProps) {
  const { icon, label, className = '', 'aria-label': ariaLabel } = props
  const classes = `${floatClassName} ${className}`.trim()

  const content = (
    <>
      {icon}
      {label ? (
        <span className="pointer-events-none absolute right-full mr-3 hidden rounded-md border border-border bg-raised px-2 py-1 text-xs font-medium whitespace-nowrap text-foreground group-hover:block">
          {label}
        </span>
      ) : null}
    </>
  )

  if (isLinkProps(props)) {
    return (
      <Link to={props.to} aria-label={ariaLabel} className={classes}>
        {content}
      </Link>
    )
  }

  const { type = 'button', onClick, disabled } = props

  return (
    <button
      type={type}
      aria-label={ariaLabel}
      className={classes}
      onClick={onClick}
      disabled={disabled}
    >
      {content}
    </button>
  )
}

/**
 * @description 判断是否为路由链接属性
 * @param props 浮动按钮属性
 * @returns 是否为路由链接属性
 */
function isLinkProps(props: FloatButtonProps): props is FloatButtonAsLink {
  return typeof props.to === 'string'
}
